import { useState, useEffect } from 'react';
import { getAllUsers } from '../../../services/firestoreService';
import { useAuth, getUserRoles } from '../../../hooks/useAuth';
import Table from '../../shared/UI/Table';
import Badge from '../../shared/UI/Badge';
import Button from '../../shared/UI/Button';
import SelectField from '../../shared/Forms/SelectField';
import ConfirmDialog from '../../shared/UI/ConfirmDialog';
import { deactivateUser } from '../../../services/userService';
import { deactivateUserDocument } from '../../../services/firestoreService';

const ROLE_LABELS = {
  analyst: 'Analyst',
  head_of_pipeline: 'Head of Pipeline',
  cio: 'CIO',
  md: 'MD',
  ic_member: 'IC Member',
  admin: 'Admin',
};

export default function UserList({ onAdd, onEdit }) {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('');
  const [error, setError] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  function load() {
    setLoading(true);
    getAllUsers()
      .then(setUsers)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  const displayed = roleFilter
    ? users.filter(u => getUserRoles(u).includes(roleFilter))
    : users;

  async function handleDeactivate() {
    if (!confirmDeactivate) return;
    setDeactivating(true);
    try {
      await deactivateUser({ uid: confirmDeactivate.id });
      await deactivateUserDocument({ uid: confirmDeactivate.id, deactivatedBy: currentUser.uid });
      setConfirmDeactivate(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeactivating(false);
    }
  }

  const columns = [
    { key: 'full_name', label: 'Full Name' },
    { key: 'email', label: 'Email' },
    {
      key: 'roles',
      label: 'Roles',
      render: row => {
        const roles = getUserRoles(row);
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {roles.length > 0
              ? roles.map(r => (
                <span key={r} className="badge badge-default" style={{ fontSize: 11 }}>
                  {ROLE_LABELS[r] || r.replace(/_/g, ' ')}
                </span>
              ))
              : '—'
            }
          </div>
        );
      },
    },
    {
      key: 'fund_access',
      label: 'Fund Access',
      render: row => (row.fund_access || []).length > 0
        ? `${(row.fund_access || []).length} fund(s)` : '—',
    },
    {
      key: 'is_active',
      label: 'Status',
      render: row => <Badge text={row.is_active !== false ? 'active' : 'inactive'} />,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: row => (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => onEdit(row)}>Edit</Button>
          {row.id !== currentUser?.uid && row.is_active !== false && (
            <Button size="sm" variant="danger" onClick={() => setConfirmDeactivate(row)}>
              Deactivate
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (error) return <div className="alert alert-error">{error}</div>;

  return (
    <>
      <div className="card">
        <div className="card-header">
          <h2>Users ({displayed.length})</h2>
          <div className="flex gap-3">
            <div style={{ width: 200 }}>
              <SelectField
                value={roleFilter}
                onChange={e => setRoleFilter(e.target.value)}
                options={[
                  { value: 'analyst', label: 'Analyst' },
                  { value: 'head_of_pipeline', label: 'Head of Pipeline' },
                  { value: 'cio', label: 'CIO' },
                  { value: 'md', label: 'MD' },
                  { value: 'ic_member', label: 'IC Member' },
                  { value: 'admin', label: 'Admin' },
                ]}
                placeholder="All roles"
              />
            </div>
            <Button variant="primary" onClick={onAdd}>+ Add New User</Button>
          </div>
        </div>
        <Table
          columns={columns}
          data={displayed}
          loading={loading}
          emptyTitle="No users found"
          emptyMessage="Add the first user account."
          emptyAction={{ label: '+ Add User', onClick: onAdd }}
        />
      </div>
      <ConfirmDialog
        isOpen={!!confirmDeactivate}
        onClose={() => setConfirmDeactivate(null)}
        onConfirm={handleDeactivate}
        title="Deactivate User"
        message={`Are you sure you want to deactivate ${confirmDeactivate?.full_name}? They will no longer be able to log in. This action can be reversed by editing the user.`}
        confirmLabel="Deactivate"
        loading={deactivating}
      />
    </>
  );
}
